"use strict"

import React, { useEffect, useReducer } from "react"
import { Modal, Button } from "react-bootstrap"
import { select, selectAll } from "d3-selection"

import { withZoomState } from "../../../config/ZoomConfigProvider"
import { getCurrentXRange } from "../../zooming/ZoomControls/utils"
import { isLTPM } from "../../../../components/shared_components_registration"
import { parseDate } from "../../../utils"
import { graphsEqual, isSupplementalCalcPlot } from "../../resources/utils"
import GraphEditForm from "./GraphEditForm"

export const registration = {
  edit_graph_title: (intl) =>
    isLTPM ? intl.formatMessage({ id: "graphing.graph.edit.menu.title" }) : "Edit Graphing Features",
  reset_graph_settings: (intl) =>
    isLTPM ? intl.formatMessage({ id: "graphing.reset.graph.settings" }) : "Reset Graph Settings",
  update_graph_settings: (intl) =>
    isLTPM ? intl.formatMessage({ id: "graphing.update.graph.settings" }) : "Update Graph Settings",
  axes_ranges_label: (intl) =>
    isLTPM ? intl.formatMessage({ id: "graphing.set.axis.ranges" }) : "Axis Ranges",
  edit_graph_trends: (intl) => (isLTPM ? intl.formatMessage({ id: "graphing.edit.trends" }) : "Graph Trends"),
  warn_required_field: (intl) =>
    isLTPM ? intl.formatMessage({ id: "general.warning.requiredFormField" }) : "* Required Field",
  min_label: (intl) => (isLTPM ? intl.formatMessage({ id: "general.min" }) : "Min"),
  max_label: (intl) => (isLTPM ? intl.formatMessage({ id: "general.max" }) : "Max"),
  search_label: (intl) => (isLTPM ? intl.formatMessage({ id: "general.search" }) : "Search"),
  title_label: (intl) => (isLTPM ? intl.formatMessage({ id: "general.title" }) : "Title"),
  y1_axis_label: (intl) => (isLTPM ? intl.formatMessage({ id: "graphing.y1.axis.label" }) : "Y1-Axis Label"),
  y2_axis_label: (intl) => (isLTPM ? intl.formatMessage({ id: "graphing.y2.axis.label" }) : "Y2-Axis Label"),
  y_axis_label: (intl) => (isLTPM ? intl.formatMessage({ id: "graphing.y.axis" }) : "Y-Axis"),
  x_axis_label: (intl) => (isLTPM ? intl.formatMessage({ id: "graphing.x.axis" }) : "X-Axis"),
  both_trend_option: (intl) => (isLTPM ? intl.formatMessage({ id: "graphing.both" }) : "Both"), // Called dynamically
  line_trend_option: (intl) => (isLTPM ? intl.formatMessage({ id: "graphing.line" }) : "Line"), // Called dynamically
  points_trend_option: (intl) => (isLTPM ? intl.formatMessage({ id: "graphing.points" }) : "Points"), // Called dynamically
  triangle_trend_option: (intl) =>
    isLTPM ? intl.formatMessage({ id: "graphing.triangles", defaultMessage: "Triangles" }) : "Triangles", // Called dynamically
  solid_trend_option: (intl) =>
    isLTPM ? intl.formatMessage({ id: "graphing.solid", defaultMessage: "Solid" }) : "Solid", // Called dynamically
  dashed_trend_option: (intl) =>
    isLTPM ? intl.formatMessage({ id: "graphing.dashed", defaultMessage: "Dashed" }) : "Dashed", // Called dynamically
  box_trend_option: (intl) => (isLTPM ? intl.formatMessage({ id: "graphing.box" }) : "Box"),
  choose_trend_color: (intl) =>
    isLTPM ? intl.formatMessage({ id: "graphing.choose.trend.color" }) : "Choose trend color",
  tag_trend_field: (intl) => (isLTPM ? intl.formatMessage({ id: "general.tag" }) : "Tag"), // Called dynamically
  units_trend_field: (intl) => (isLTPM ? intl.formatMessage({ id: "general.units" }) : "Units"), // Called dynamically
  label_trend_field: (intl) => (isLTPM ? intl.formatMessage({ id: "general.label" }) : "Label"), // Called dynamically
  colour_trend_field: (intl) => (isLTPM ? intl.formatMessage({ id: "graphing.color" }) : "Color"), // Called dynamically
  style_trend_field: (intl) => (isLTPM ? intl.formatMessage({ id: "graphing.style" }) : "Style"), // Called dynamically
  axis2_trend_field: (intl) => (isLTPM ? intl.formatMessage({ id: "graphing.y.axis" }) : "Y-Axis"), // Called dynamically
  disabled_trend_field: (intl) =>
    isLTPM ? intl.formatMessage({ id: "graphing.trend.disabled" }) : "Disabled", // Called dynamically
  missing_value_alt: (intl) => (isLTPM ? "-" : "None"),
  strokeWidth_trend_field: (intl) =>
    isLTPM
      ? `${intl.formatMessage({ id: "graphing.line" })} (${intl.formatMessage({ id: "general.size" })})`
      : "Line (Size)", // Called dynamically
  strokeModifier_trend_field: (intl) =>
    isLTPM
      ? `${intl.formatMessage({ id: "graphing.line" })} (${intl.formatMessage({ id: "graphing.style" })})`
      : "Line (Style)", // Called dynamically
  pointSize_trend_field: (intl) =>
    isLTPM
      ? `${intl.formatMessage({ id: "graphing.points" })} (${intl.formatMessage({ id: "general.size" })})`
      : "Points (Size)", // Called dynamically
  pointModifier_trend_field: (intl) =>
    isLTPM
      ? `${intl.formatMessage({ id: "graphing.points" })} (${intl.formatMessage({ id: "graphing.style" })})`
      : "Points (Style)", // Called dynamically
  editingAllowed: (op) => {
    return (
      (!op.special || ["multi", "site-wide", "microbial-community"].includes(op.special)) &&
      !isSupplementalCalcPlot(op)
    )
  },
}

const initialState = {
  isLoading: false,
  title: "",
  axis1label: "",
  axis2label: "",
  axis1domain: [],
  axis2domain: [],
  xaxisdomain: [],
  observations: [],
  tags: [],
  microbes: [],
}

const getInitialResourceState = (graphKey, dispatch, axesAndRanges, zoomRanges, timeExtractor, baseTime) => {
  selectAll(".requiredFieldWarning").style("display", "none")

  const input_options = ["title", "axis1label", "axis2label"]
  input_options.forEach((opt) => {
    opt in graphKey ? dispatch({ type: "field", value: graphKey[opt], field: opt }) : null
  })

  const axis_options = ["axis1domain", "axis2domain", "xaxisdomain"]
  axis_options.forEach((opt) => {
    dispatch({
      type: "field",
      value: opt in graphKey ? graphKey[opt] : [null, null],
      field: opt,
    })
  })

  const list_options = ["observations", "tags", "microbes"]
  list_options.forEach((opt) => {
    if (opt in graphKey) {
      dispatch({ type: "field", value: [], field: opt })
      graphKey[opt].forEach((resource) => {
        dispatch({ type: "new-list-entry", value: resource, field: opt })
      })
    }
  })

  dispatch({
    type: "field",
    value: getCurrentXRange(axesAndRanges, zoomRanges, timeExtractor, baseTime),
    field: "xaxisdomain",
  })
}

function formReducer(state, action) {
  switch (action.type) {
    case "field": {
      return {
        ...state,
        [`${action.field}`]: action.value,
      }
    }
    case "new-list-entry": {
      return {
        ...state,
        [`${action.field}`]: [...state[`${action.field}`], action.value],
      }
    }
    case "edit-list-entry": {
      let opt_list = Object.assign([], state[`${action.field}`])
      let entry_info = { ...opt_list[action.index] }
      entry_info[action.list_field] = action.value
      opt_list[action.index] = entry_info

      return {
        ...state,
        [`${action.field}`]: opt_list,
      }
    }
    case "submit": {
      return {
        ...state,
        isLoading: true,
      }
    }
    case "invalid-form": {
      return {
        ...state,
        isLoading: false,
      }
    }
  }
}

function GraphEditModal({
  show,
  onHide,
  graphKey,
  updateGraph,
  timeExtractor,
  axesAndRanges,
  zoomConfig,
  baseTime,
  intl,
}) {
  const zoomRanges = zoomConfig.zoomForPlot(null, graphKey) // We only care about the X-Axis zoom which is always shared across all plots on a page so we can pass a null id here
  const [state, dispatch] = useReducer(formReducer, { ...initialState })
  useEffect(() => {
    getInitialResourceState(graphKey, dispatch, axesAndRanges, zoomRanges, timeExtractor, baseTime)
  }, [])

  const validateForm = (e) => {
    e.preventDefault()
    dispatch({ type: "submit" })

    let valid_form = true
    let validate_input_fields = {
      title: state.title,
    }
    Object.keys(validate_input_fields).forEach((field) => {
      if (validate_input_fields[field] === "") {
        select(`#${field}_err_message`).style("display", "block")
        valid_form = false
      }
    })

    if (valid_form) {
      let info = {
        ...graphKey,
        ...validate_input_fields,
        axis1label: state.axis1label,
        axis2label: state.axis2label,
        axis1domain: state.axis1domain,
        axis2domain: state.axis2domain,
      }
      if (graphKey.special === "microbial-community") {
        info["microbes"] = state.microbes
      } else if (graphKey.special === "site-wide") {
        info["tags"] = state.tags
      } else {
        info["observations"] = state.observations
      }

      if (state.xaxisdomain.some((x) => x || x === 0)) {
        const xAxisRange =
          timeExtractor.name !== "Day" ? state.xaxisdomain.map((x) => parseDate(x)) : state.xaxisdomain
        zoomConfig.zoomTime(
          xAxisRange[0] ? timeExtractor.difference(xAxisRange[0], baseTime) : xAxisRange[0],
          xAxisRange[1] ? timeExtractor.difference(xAxisRange[1], baseTime) : xAxisRange[1],
        )
      }

      if (!graphsEqual(graphKey, info)) {
        updateGraph(info)
      }
      onHide()
    } else {
      dispatch({ type: "invalid-form" })
    }
  }

  return (
    <Modal
      size={"lg"}
      dialogClassName={"definitions-modal"}
      show={show}
      onHide={onHide}
      backdrop={"static"}
      style={{ overflowX: "scroll", overflowY: "scroll" }}
      enforceFocus={false} // To allow TrendColorEditor Popover input element to have focus
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {registration["edit_graph_title"](intl)}:&nbsp;
          <span>{graphKey.title}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <GraphEditForm
          intl={intl}
          graphKey={graphKey}
          state={state}
          dispatch={dispatch}
          timeExtractor={timeExtractor}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          type={"reset"}
          disabled={state.isLoading}
          variant={"outline-primary"}
          onClick={(e) => {
            e.preventDefault()
            getInitialResourceState(graphKey, dispatch, axesAndRanges, zoomRanges, timeExtractor, baseTime)
          }}
        >
          {registration["reset_graph_settings"](intl)}
        </Button>
        <Button
          type={"submit"}
          disabled={state.isLoading}
          variant={"primary"}
          onClick={(e) => validateForm(e)}
        >
          {registration["update_graph_settings"](intl)}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default withZoomState(GraphEditModal)
